<template>
  <div class="faculties">
    <PageHeader
      class="faculties__header"
      :item-count="total"
      :show-filter="false"
      :show-add-item-button="false"
    />
    <h3
      v-if="selectedNiche.name"
      class="faculties__niche-name"
    >
      Cборные {{ selectedNiche.name }}
    </h3>
    <div class="faculties__list">
      <FacultyCard
        v-for="faculty in faculties"
        :key="faculty.id"
        class="faculties__list-item"
        :faculty="faculty"
        :is-user-faculty="userFaculty && faculty.id === userFaculty.id"
        readonly
        @click="onClick(faculty.id)"
        @changeFaculty="openModal(faculty.id)"
      />
      <template v-if="loading">
        <FacultyCard
          class="faculties__list-item"
          preloader
        />
        <FacultyCard
          class="faculties__list-item"
          preloader
        />
      </template>
    </div>
    <NoResults
      v-if="!loading && !faculties.length"
      class="faculties__splash"
    />
    <FacultyAside
      :is-opened="asideOpened"
      :value="selectedFaculty"
      :status="selectedFacultyStatus"
      :current-faculty="userFaculty"
      :faculty-changes="facultyChangeCount"
      @enlist="openModal(selectedFaculty.id)"
      @close="closeAside"
    />
    <EnlistModal
      :show="modalOpened"
      :show-cancel="modalMode !== 'congratulate'"
      :loading="enlistLoading"
      @cancel="closeModal"
      @confirm="accept"
    >
      <template
        v-if="modalContent.icon === 'warning'"
        #header
      >
        <WarningSplashIcon />
      </template>
      <template
        v-else-if="modalContent.icon === 'garland'"
        #header
      >
        <GarlandIcon />
      </template>
      <template #default>
        <h3>{{ modalContent.header }}</h3>
        <p>{{ modalContent.text }}</p>
      </template>
      <template #cancel-btn>
        Отменить
      </template>
      <template #accept-btn>
        {{ modalContent.accept }}
      </template>
    </EnlistModal>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import PageHeader from '../../../components/PageHeader';
import FacultyCard from '../../../components/FacultyCard';
import FacultyAside from '../../components/FacultyAside';
import NoResults from '../../../components/NoResults';
import WarningSplashIcon from '@/components/Icons/WarningSplashIcon';
import GarlandIcon from '@/components/Icons/GarlandIcon';
import EnlistModal from '@/views/Faculties/user/components/EnlistModal';

export default {
  name: 'NicheFaculties',
  components: {
    EnlistModal,
    PageHeader,
    FacultyCard,
    FacultyAside,
    NoResults,
    WarningSplashIcon,
    GarlandIcon
  },
  props: {
    id: {
      type: String,
      default: '0'
    }
  },
  data: function () {
    return {
      asideOpened: false,
      modalOpened: false,
      modalMode: 'enlist', // 'enlist' | 'congratulate' | 'switch' | 'leave' | 'warn'
      lastMode: undefined
    };
  },
  computed: {
    ...mapState('auth', {
      facultyChangeCount: state => state.data.user.facultyChangeCount
    }),
    ...mapState('faculties/user/faculties', {
      faculties: state => state.faculties,
      selectedCategory: state => state.selectedCategory,
      selectedNiche: state => state.selectedNiche,
      selectedFaculty: state => state.selectedFaculty,
      selectedFacultyStatus: state => state.selectedFacultyStatus,
      userFaculty: state => state.userFaculty,
      total: state => state.meta.faculties.total,
      loading: state => state.meta.faculties.loading,
      enlistLoading: state => state.meta.enlistLoading
    }),
    modalContent() {
      return {
        icon: this.modalMode === 'congratulate' ? 'garland' : 'warning',
        header:
          this.modalMode === 'congratulate' ? 'Поздравляем, ты вступил в сборную!' : undefined,
        text:
          this.modalMode === 'enlist'
            ? this.selectedFacultyStatus.confirmText
            : this.modalMode === 'warn'
              ? 'Ты уже вступил в другую сборную. Для смены сборной обратись к своему менеджеру.'
              : this.modalMode === 'congratulate'
                ? this.lastMode === 'enlist' && this.facultyChangeCount === 0
                  ? 'Ты можешь просматривать остальные сборные, и поменять свою сборную 1 раз.'
                  : 'Ты можешь просматривать остальные сборные, но не сможешь больше в них вступить.'
                : this.modalMode === 'switch'
                  ? this.selectedFacultyStatus.confirmText
                  : this.modalMode === 'leave'
                    ? 'Ты уверен, что хочешь выйти из сборной? После выхода ты можешь вырать сборную 1 раз.'
                    : '',
        accept: ['enlist', 'switch'].includes(this.modalMode)
          ? 'Да, вступить'
          : this.modalMode === 'leave'
            ? 'Да, выйти'
            : 'Хорошо'
      };
    }
  },
  watch: {
    'selectedNiche': function() {
      if(this.selectedCategory.name) {
        this.$route.meta.breadcrumbs = [
          { name: 'Сборные', path: '/faculties/user' },
          {
            name: this.selectedCategory.name,
            path: `/faculties/user/category/${this.selectedCategory.id}`
          },
          { name: this.selectedNiche.name }
        ];
      } else {
        this.$route.meta.breadcrumbs = [
          { name: 'Сборные', path: '/faculties/user' },
          { name: this.selectedNiche.name }
        ];
      }
      this.updateRouterUpdatedFlagMut();
    }
  },
  created() {
    this.clearFaculties();
    if (!this.loading) {
      this.getFaculties({ niche: this.id });
    }
    if (!this.userFaculty?.id) {
      this.getUserFaculty();
    }
  },
  methods: {
    ...mapActions('faculties/user/faculties', [
      'getFaculties',
      'getUserFaculty',
      'getSelectedFaculty',
      'enlistFaculty',
      'leaveFaculty'
    ]),
    ...mapMutations('faculties/user/faculties', ['clearFaculties', 'setSelectedFaculty']),
    ...mapMutations('route', ['updateRouterUpdatedFlagMut']),
    openAside() {
      this.asideOpened = true;
    },
    closeAside() {
      this.asideOpened = false;
      this.setSelectedFaculty({
        faculty: undefined,
        status: { enabled: false, text: '' }
      });
    },
    openModal(id) {
      this.modalMode =
        this.userFaculty?.id === id ? 'leave' : this.userFaculty?.id && id ? 'switch' : 'enlist';
      this.modalOpened = true;
    },
    closeModal() {
      this.modalOpened = false;
    },
    async accept() {
      if (this.modalMode === 'enlist') {
        const response = await this.enlistFaculty(this.selectedFaculty?.id);
        if (response.status === 204) {
          this.lastMode = 'enlist';
          this.modalMode = 'congratulate';
        }
      } else if (this.modalMode === 'switch') {
        const response = await this.enlistFaculty(this.selectedFaculty?.id);
        console.log(response);
        if (response.status === 204) {
          this.lastMode = 'switch';
          this.modalMode = 'congratulate';
        }
      } else if (this.modalMode === 'leave') {
        await this.leaveFaculty();
        this.closeModal();
      } else {
        this.closeModal();
      }
    },
    onClick(id) {
      this.openAside();
      this.setSelectedFaculty({
        faculty: this.faculties.find(f => f.id === id),
        status: { enabled: false, text: '' }
      });
      this.getSelectedFaculty(id);
    }
  }
};
</script>

<style lang="scss" scoped>
.faculties {
  display: flex;

  &__header {
    order: 1;

    @media screen and (max-width: 960px) {
      order: 2;
    }
  }

  &__niche-name {
    font-size: 18px;
    margin-top: 8px;
    margin-bottom: 16px;
    order: 2;

    @media screen and (max-width: 960px) {
      order: 1;
    }
  }
  &__list {
    display: flex;
    flex-direction: column;
    order: 3;

    &-item {
      margin-bottom: 20px;
    }
  }

  &__splash {
    order: 4;
  }
}
</style>
